import PageForm from "./PageForm";
import PageChat from "./PageChat";
import {
  fetchTopic,
  setTopicField,
  fetchTopicSubscription,
} from "../../redux/slices/topicSlice";
import { fetchChannel } from "../../redux/slices/channelSlice";
import { fetchTopics, clearTopicUnread } from "../../redux/slices/reorderTopicSlice";
import { postRequestAuthenticated } from "../../services/rest";
import TopicHomeSkeleton from "./../skeleton/Topic/TopicHomeSkeleton";
import InviteTopicPage from "./../Channel/InviteTopicPage";
import { connectSocketWithUser } from "../../utils/socket";
import { getAppPrefix, getEmbedLastKey } from "./../EmbedChannels/utility/embedHelper";

import {
  React,
  useState,
  useEffect,
  useNavigate,
  useDispatch,
  useSelector,
  useParams,
  useLocation,
  useSearchParams,
} from "../../globals/imports";

const PageHome = () => {
  const { channelId, topicId } = useParams();

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const dispatch = useDispatch();
  const topic = useSelector((state) => state.topic);
  const topicStatus = useSelector((state) => state.topic.topicstatus);
  const channel = useSelector((state) => state.channel);
  const myData = useSelector((state) => state.myData);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  // const [loading, setLoading] = useState(true);
  const channelLoading = useSelector((state) => state.channel.loading);
  const topics = useSelector((state) => state.reorderTopic.topics) || [];
  const location = useLocation();
  const params = useParams();
  const username = params.username;
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("code");
  const navigate = useNavigate();
  const toggleBottomSheet = () => {
    setIsBottomSheetOpen(!isBottomSheetOpen);
  };
  const closeBottomSheet = () => setIsBottomSheetOpen(false);

  const loading = topicStatus === "loading" || channelLoading;
  // Use the backend relationship flag; fall back to false. (channel.members is
  // membership docs, not user ids, so .includes never matched.)
  const isChannelMember = !!channel?.isMember;
  const isTopicOwner =
    (topic.user?._id || topic.user)?.toString() === myData?._id?.toString();
  const isUnauthorized =
    (!isChannelMember && !isTopicOwner) ||
    (channel.visibility === "me" && !isTopicOwner) ||
    (channel.visibility === "invite" && !isTopicOwner);

  useEffect(() => {
    dispatch(fetchTopic(topicId));
    dispatch(fetchChannel(channelId));
    dispatch(fetchTopics(channelId)); // populate the topic switcher
    // dispatch(fetchTopicSubscription(topicId));
  }, [topicId, channelId, dispatch]);

  useEffect(() => {
    if (isLoggedIn && myData?._id) {
      connectSocketWithUser(myData._id);
    }
  }, [isLoggedIn, myData?._id]);

  // Opening a topic marks it read -> clear its unread dot (locally + server).
  useEffect(() => {
    if (!topicId || !myData?._id) return;
    dispatch(clearTopicUnread(topicId));
    postRequestAuthenticated("/mark/as/read", { topicId }).catch(() => {});
  }, [topicId, myData?._id, dispatch]);

  // Remember the last channel/topic the user opened (per business/domain) so the
  // embed reopens here next time instead of the default channel.
  useEffect(() => {
    // Wait until the channel has loaded before deciding — on first mount
    // `channel` is empty and would transiently look unauthorized.
    if (!channelId || loading || !channel?._id) return;
    try {
      const key = getEmbedLastKey();
      if (!key) return;
      if (isUnauthorized) {
        // Only requested / not a member: don't remember a place we can't return
        // to, and drop any stale saved entry for this channel so the next open
        // falls back to the default channel instead of erroring on this topic.
        const saved = JSON.parse(localStorage.getItem(key) || "{}");
        if (saved.channel === channelId) localStorage.removeItem(key);
        return;
      }
      localStorage.setItem(
        key,
        JSON.stringify({ channel: channelId, topic: topicId || "" })
      );
    } catch (e) {}
  }, [channelId, topicId, loading, channel, isUnauthorized]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLoggedIn) {
        navigate(
          `${getAppPrefix()}/get-started?redirect=${getAppPrefix()}/user/${username}/channel/${channelId}/c-id/topic/${topicId}`
        );
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [isLoggedIn, navigate, username, channelId, topicId]);

  if (inviteCode) {
    dispatch(setTopicField({ field: "loadingStatus", value: "idle" }));
    return (
      <InviteTopicPage
        code={inviteCode}
        topicId={topicId}
        username={username}
        channelId={channelId}
      />
    );
  }

  if (loading) {
    return <TopicHomeSkeleton />;
  }

  if (isLoggedIn && isUnauthorized && myData._id) {
    return (
      <div className="w-full h-screen bg-theme-secondaryBackground text-center flex flex-col justify-center font-bold text-lg text-theme-secondaryText">
        <p>You don't have authorization to this topic</p>
        <div
          className="cursor-pointer text-sm font-normal text-center mt-4 rounded-lg mx-auto
         bg-theme-secondaryText py-2 px-3 text-theme-primaryBackground w-max"
          onClick={() =>
            navigate(`${getAppPrefix()}/user/${username}/channel/${channelId}`)
          }
        >
          Return to Channel Page
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-theme-secondaryBackground flex flex-row ">
      <div className="flex-1 overflow-hidden flex flex-col">
        {topics.length > 0 && (
          <div className="flex shrink-0 flex-row gap-2 overflow-x-auto bg-theme-secondaryBackground px-3 pb-2 pt-1 custom-scrollbar">
            {topics.map((t) => {
              const tid = t._id || t;
              const active = String(tid) === String(topicId);
              return (
                <button
                  key={tid}
                  onClick={() =>
                    navigate(
                      `${getAppPrefix()}/user/${username}/channel/${channelId}/c-id/topic/${tid}`
                    )
                  }
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                    active
                      ? "bg-theme-accent text-theme-accentText"
                      : "border border-theme-chatDivider text-theme-secondaryText hover:bg-theme-tertiaryBackground"
                  }`}
                >
                  #{t.name}
                  {t.hasUnread && !active && (
                    <span className="h-2 w-2 flex-shrink-0 rounded-full bg-theme-accent" />
                  )}
                </button>
              );
            })}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-hidden">
        {/* <PageHeader
          channelName={channelName}
          topic={topic}
          // toggleSidebar={toggleSidebar}
          toggleBottomSheet={toggleBottomSheet}
          isOpen={isBottomSheetOpen} 
          username={username}
          channelId={channelId}
          // isSidebarOpen={isSidebarOpen}
        /> */}
        <PageChat
          topicId={topicId}
          topic={topic}
          channelId={channelId}
          isLoggedIn={isLoggedIn}
          myData={myData}
          channel={channel}
          channelName={channel.name}
          toggleBottomSheet={toggleBottomSheet}
          isOpen={isBottomSheetOpen}
          username={username}
        />
        </div>
      </div>

      <div className="">
        <PageForm
          isOpen={isBottomSheetOpen}
          onClose={closeBottomSheet}
          topic={topic}
          channel={channel}
        />
      </div>
    </div>
  );
};

export default PageHome;
